import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faRandom } from '@fortawesome/free-solid-svg-icons';
import DownloadButton from '../DownloadButton/DownloadButton';
import Button from '../ui/Button';

const SHOWN = 12;

const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
};

// Bento span pattern — cycles across the 12 tiles so a couple read as large
// heroes and the rest fill in around them. Each entry is a set of grid-span
// utilities applied per tile index (mod pattern length).
const BENTO_SPANS = [
    'sm:col-span-2 sm:row-span-2', // big hero
    '',
    '',
    'sm:row-span-2',               // tall
    '',
    'sm:col-span-2',               // wide
    '',
    'sm:col-span-2 sm:row-span-2', // big hero
    '',
    '',
    'sm:row-span-2',               // tall
    '',
];

const FreeDownloads = () => {
    const [pool, setPool] = useState([]);
    const [shown, setShown] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const params = new URLSearchParams({
            key: process.env.REACT_APP_PIXABAY_API_KEY,
            image_type: 'photo',
            order: 'popular',
            editors_choice: 'true',
            safesearch: 'true',
            per_page: '60',
            pretty: 'true',
        });
        fetch(`https://pixabay.com/api/?${params.toString()}`)
            .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
            .then((data) => {
                const hits = data.hits || [];
                setPool(hits);
                setShown(shuffle(hits).slice(0, SHOWN));
                setLoading(false);
            })
            .catch((err) => {
                console.log('Free downloads failed', err);
                setLoading(false);
            });
    }, []);

    const reshuffle = useCallback(() => {
        setShown(shuffle(pool).slice(0, SHOWN));
    }, [pool]);

    if (!loading && shown.length === 0) return null;

    return (
        <section className="w-full py-10 sm:py-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
                <div>
                    <h2 className="font-serif text-3xl sm:text-4xl font-medium text-gray-900 dark:text-white">
                        Free downloads
                    </h2>
                    <p className="text-gray-500 dark:text-gray-400 mt-1">A fresh mix every time — hover any image to grab it.</p>
                </div>
                <div className="flex items-center gap-4 shrink-0">
                    <Button
                        variant="outline"
                        onClick={reshuffle}
                        disabled={loading || pool.length === 0}
                    >
                        <FontAwesomeIcon icon={faRandom} /> Shuffle
                    </Button>
                    <Link
                        to="/photos"
                        className="hidden sm:inline-flex items-center min-h-[44px] px-2 gap-1 rounded-full text-brand-600 dark:text-brand-400 font-semibold hover:text-brand-700 dark:hover:text-brand-500 transition-colors"
                    >
                        View all <FontAwesomeIcon icon={faArrowRight} />
                    </Link>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 sm:grid-cols-4 auto-rows-[9rem] sm:auto-rows-[10rem] gap-3 sm:gap-4">
                {loading
                    ? Array.from({ length: SHOWN }).map((_, i) => (
                          <div
                              key={i}
                              className={`rounded-2xl bg-gray-200 dark:bg-gray-800 animate-pulse ${BENTO_SPANS[i % BENTO_SPANS.length]}`}
                          />
                      ))
                    : shown.map((img, i) => (
                          <div
                              key={img.id}
                              className={`group relative block overflow-hidden rounded-2xl bg-gray-100 dark:bg-gray-800 ${BENTO_SPANS[i % BENTO_SPANS.length]}`}
                          >
                              <img
                                  src={img.webformatURL}
                                  alt={`by ${img.user}`}
                                  loading="lazy"
                                  className="absolute inset-0 w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                              />
                              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gradient-to-t from-black/60 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                                  <span className="text-white text-sm font-medium drop-shadow">by {img.user}</span>
                                  <div className="pointer-events-auto">
                                      <DownloadButton url={img.largeImageURL || img.webformatURL} filename={`pixiy-${img.id}.jpg`} />
                                  </div>
                              </div>
                          </div>
                      ))}
            </div>
        </section>
    );
};

export default FreeDownloads;
